<template>
  <v-dialog
    v-model="dialog"
    persistent
    :max-width="dialogSize"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        icon
        v-on="on"
      >
        <v-icon color="primary">
          mdi-database-plus
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      :title="'Exam Details '+ '('+exam.name+')'"
      text="Exam Details"
      class="mb-0"
    >
      <v-container
        v-if="dialog"
        class="dialog-size"
      >
        <v-row>
          <v-col
            :cols="columnSize"
            class="divider"
          >
            <v-row>
              <h5 class="mb-0 font-weight-bold">
                Exam Participants:
              </h5>
            </v-row>
            <v-row>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-text-field
                  v-model="searchInput" 
                  placeholder="Filter employee by name"
                  append-icon="search"
                  label="Search Employee"
                  hide-no-data
                  hide-selected
                />
              </v-col>
              <v-col
                cols="12"
                class="py-0"
              >
                <v-autocomplete
                  v-model="trainingSessionFilter"
                  :items="trainingSessions"
                  label="Training Session"
                  item-text="name"
                  item-value="id"
                  placeholder="Filter employee by training session"
                  clearable
                  hide-selected
                  deletable-chips
                  chips
                  multiple
                />
              </v-col>
              <v-col
                cols="12"
                class="pl-0"
              >
                <v-data-table
                  v-model="selectedParticipants"
                  show-select
                  :headers="headers"
                  :items="filteredParticipants"
                  class="elevation-1"
                  item-value="employee_uid"
                  item-key="employee.person.name"
                  hide-default-footer
                  fixed-header
                  dense
                  disable-pagination
                  :height="dataTableHeight"
                  :loading="tableLoading"
                >
                  <template v-slot:item.participated="{ item }">
                    <div>
                      <v-icon 
                        v-if="item.participated " 
                        color="success"
                      >
                        mdi-check
                      </v-icon>
                    </div>
                  </template>
                </v-data-table>
              </v-col>
              <v-col
                cols="12"
                class="pl-0"
              >
                <v-btn
                  :loading="removeParticipant"
                  color="error"
                  :disabled="(today>= exam.start_date) || selectedParticipants.length < 1" 
                  @click="removeParticipants()"
                >
                  remove
                </v-btn>
                <v-btn
                  class="float-right"
                  :loading="addParticipant"
                  color="success"
                  :disabled="(today>= exam.start_date) || selectedParticipants.length < 1" 
                  @click="addParticipants()"
                >
                  Add
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            v-if="questionSection"
            cols="8"
          >
            <div
              v-if="loadQuestion"
              class="progress-circular-container"
            >
              <v-progress-circular
                :size="50"
                color="primary"
                indeterminate
              />
            </div>

            <div v-else>
              <div v-if="!edit && questionSection">
                <v-row v-if="!addQuestionSection">
                  <v-spacer />
                  <v-btn
                    v-if="!(today>= exam.start_date)"
                    color="success"
                    @click="addQuestionSection = true"
                  >
                    Add Questions
                  </v-btn>
                </v-row>

                <v-row
                  v-if="addQuestionSection"
                  class="elevation-3 mx-1"
                >
                  <v-col cols="12">
                    <tiptap-vuetify
                      v-model="content"
                      v-validate="'required'"
                      :extensions="extensions"
                      class="full-width text-editor-toolbar-height"
                      :error-messages="errors.collect('content')"
                      data-vv-name="content"
                    />
                    <span class="red--text text--lighten-1 caption">{{ errors.first('content') }}</span>
                  </v-col>
            
                  <v-col cols="6">
                    <v-text-field
                      v-model="mark"   
                      v-validate="'required|min:1'"
                      label="Mark"
                      type="number"
                      :error-messages="errors.collect('mark')"
                      data-vv-name="mark"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-autocomplete
                      v-model="questionType"
                      v-validate="'required'"
                      label="Question Type"
                      :items="questionTypes"
                      item-text="name"
                      item-value="id"
                      :error-messages="errors.collect('question type')"
                      data-vv-name="question type"
                      class="drop-down"
                      @change="clearAnswer(false)"
                    />
                  </v-col>

                  <v-col
                    v-if="questionType"
                    cols="12"
                    class="pt-0"
                  >
                    <span class="font-weight-bold pr-2">Answers:</span>
                    <v-tooltip
                      v-if="questionType === 1"
                      bottom
                    >
                      <template v-slot:activator="{ on: tooltip }">
                        <v-icon
                          color="primary"
                          v-on="{ ...dialog, ...tooltip }"
                          @click="addRow(false)"
                        >
                          mdi-plus
                        </v-icon>
                      </template>
                      <span>Add new row</span>
                    </v-tooltip>

                    <div class="px-5 choices-scrollbar choices-container">
                      <span
                        v-if="questionType === 1"
                        class="font-12"
                      >Please select the answer for this question</span>
                      <v-radio-group
                        v-if="questionType === 1"
                        v-model="answerIndex"
                        v-validate="'required'"
                        class="radio-group-full-width"
                        :error-messages="errors.collect('answerIndex')"
                        data-vv-as="answer"
                        data-vv-name="answerIndex"
                      >
                        <v-row
                          v-for="(item,index) in answerChoices"
                          :key="index"
                          class="display-flex"
                        >
                          <v-col
                            cols="1"
                            class="py-0 text-center ma-auto pl-5"
                          >
                            <v-radio
                              :value="index"
                            />
                          </v-col>
                          <v-col
                            cols="10"
                            class="py-0"
                          >
                            <v-text-field
                              v-model="item.content"
                              v-validate="'required'"
                              :name="'content' + index"
                              :error-messages="errors.collect('content' + index)"
                              data-vv-as="choice"
                            />
                          </v-col>
                          <v-col
                            cols="1"
                            class="text-center ma-auto"
                          >
                            <v-icon
                              color="error"
                              @click="deleteRow(index,false)"
                            >
                              mdi-window-close
                            </v-icon>
                          </v-col>
                        </v-row>
                      </v-radio-group>

                      <div v-if="questionType ===2">
                        <v-text-field
                          v-model="structureAnswer"
                          v-validate="'required'"
                          :error-messages="errors.collect('structureAnswer')"
                          data-vv-name="structureAnswer"
                          data-vv-as="answer"
                        />
                      </div>
                    </div>
                  </v-col>

                  <v-col
                    cols="12"
                    class="text-right"
                  >
                    <v-btn
                      color="success"
                      class="mr-1"
                      @click="save(false)"
                    >
                      Save and View
                    </v-btn>
                    <v-btn
                      color="success"
                      class="mr-1"
                      @click="save(true)"
                    >
                      Save and Add Next
                    </v-btn>
                    <v-btn
                      text
                      @click="clearQuestionForm()"
                    >
                      Cancel
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row
                  v-else
                  class="question-container question-container-scrollbar"
                >
                  <v-col cols="12">
                    <v-card
                      v-for="(question,index) in questions"
                      :key="index"
                      class="mb-3 elevation-2"
                    >
                      <v-card-title>
                        Question {{ index + 1 }}
                        <v-spacer />
                        <span v-if="!(today>= exam.start_date)">
                          <v-btn
                            v-if="$hasPermissionTo(['update exam questions'])"
                            icon
                            @click="editQuestion(question)"
                          >
                            <v-icon color="primary">
                              mdi-pencil
                            </v-icon>
                          </v-btn>
                          <setup-training-exams-dialog-delete-question 
                            :question="question"
                            @deleted="loadQuestions"
                          />
                        </span>
                      </v-card-title>
             
                      <v-card-text
                        class="pb-0"
                        v-html="question.content"
                      />

                      <v-card-text
                        v-for="(answer,index) in question.answer_choices"
                        :key="index"
                        :class="{
                          'correct-answer py-1 font-weight-bold':answer.index === question.answer_index,
                          'py-1':answer.index !== question.answer_index
                        }"
                      >
                        <span v-if="question.question_type_id === 1">
                          {{ String.fromCharCode(97 + answer.index).toUpperCase() }}.
                        </span>
                        {{ answer.content }}
                        <span v-if="answer.index === question.answer_index"><i>(ANSWER)<i /></i></span>
                      </v-card-text>
      
                      <v-card-text class="text-right">
                        <span class="px-2 "><i> Marks: {{ question.mark }}</i></span>
                        <span class="px-2 border"><i>Weightage: {{ question.weightage }}%</i></span>
                        <span class="px-2"><i>Type: {{ question.question_type.name }}</i></span>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <v-row
                v-if="edit"
                class="elevation-3 mx-1"
              >
                <v-col cols="12">
                  <tiptap-vuetify
                    v-model="localExamQuestion.content"
                    :extensions="extensions"
                    class="full-width text-editor-toolbar-height"
                  />
                </v-col>
 
                <v-col cols="6">
                  <v-text-field
                    v-model="localExamQuestion.mark" 
                    v-validate="'required|min:1'"
                    label="Mark"
                    type="number"
                    :error-messages="errors.collect('mark')"
                    data-vv-name="mark"
                  />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete 
                    v-model="localExamQuestion.question_type_id"
                    label="Question Type"
                    :items="questionTypes"
                    item-text="name"   
                    item-value="id"
                    class="drop-down"
                    @change="clearAnswer(true)"
                  />
                </v-col>

                <v-col 
                  v-if="localExamQuestion.question_type_id"
                  cols="12"
                  class="pt-0"
                >
                  <span class="font-weight-bold pr-2">Answers:</span>
                  <v-tooltip
                    v-if="localExamQuestion.question_type_id === 1"
                    bottom
                  >
                    <template v-slot:activator="{ on: tooltip }">
                      <v-icon
                        color="primary"
                        v-on="{ ...dialog, ...tooltip }"
                        @click="addRow(true)"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>Add new row</span>
                  </v-tooltip>
   
                  <v-radio-group
                    v-if="localExamQuestion.question_type_id === 1"
                    v-model="localExamQuestion.answer_index"
                    v-validate="'required'"
                    class="radio-group-full-width"  
                    :error-messages="errors.collect('answerIndex')"
                    data-vv-as="answer"
                    data-vv-name="answerIndex"
                  >
                    <v-row 
                      v-for="(item,index) in localExamQuestion.answer_choices"
                      :key="index"
                    >
                      <v-col
                        cols="1"
                        class="py-0 text-center ma-auto"
                      >
                        <v-radio
                          :value="index"
                          class="ml-6"
                        />
                      </v-col>
                      <v-col
                        cols="10"
                        class="py-0"
                      >
                        <v-text-field
                          v-model="item.content"
                          v-validate="'required'"
                          :name="'content' + index"
                          :error-messages="errors.collect('content' + index)"
                          data-vv-as="choice" 
                        />
                      </v-col>
                      <v-col
                        cols="1"
                        class="text-center ma-auto"
                      >
                        <v-icon
                          color="error"
                          @click="deleteRow(index,true)"
                        >
                          mdi-window-close
                        </v-icon>
                      </v-col>
                    </v-row> 
                  </v-radio-group>

                  <div v-if="localExamQuestion.question_type_id ===2">
                    <div 
                      v-for="(item,index) in localExamQuestion.answer_choices"
                      :key="index"
                    >
                      <v-text-field
                        v-model="item.content"
                        v-validate="'required'"
                        :error-messages="errors.collect('structureAnswer')"
                        data-vv-name="structureAnswer"
                        data-vv-as="answer"
                      />
                    </div>
                  </div>
                </v-col>
                <v-col
                  cols="12"
                  class="text-right"
                >
                  <v-btn
                    color="success"
                    class="mr-1"
                    @click="update"
                  >
                    Update
                  </v-btn>
                  <v-btn
                    text
                    @click="edit = false"
                  >
                    Cancel
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-container>
     
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="closeDialog()"
        >
          Close
        </v-btn>
      </v-card-actions> 
    </material-card>
  </v-dialog>
</template>

<script>
  import { TiptapVuetify, Heading, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, HardBreak } from 'tiptap-vuetify'

  export default {
    components: {
      TiptapVuetify,
      SetupTrainingExamsDialogDeleteQuestion: () => import('@/components/setup/training/exams/DialogDeleteQuestion'),
    },

    inject: ['$validator'],
    props: {
      exam: {
        type: Object,
        required: true,
      },
      open: {
        type: Boolean,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        tableLoading: false,
        addParticipant: false,
        removeParticipant: false,
        addQuestionSection: false,
        questionSection: false,
        loadQuestion: false,
        edit: false,
        questionTypes: [],
        questionType: null,
        content: null,
        mark: null,
        answerIndex: null,
        answerChoices: [{ content: null }, { content: null }],
        structureAnswer: null,
        questions: [],
        localExamQuestion: {},
        extensions: [
          Link,
          Underline,
          Italic,
          ListItem,
          BulletList,
          OrderedList,
          [Heading, {
            options: {
              levels: [1, 2, 3],
            },
          }],
          Bold,
          HardBreak,
        ],
        headers: [
          { text: 'Name', value: 'employee.person.name', align: 'left', sortable: false },
          { text: 'Added to Exam', value: 'participated', align: 'center' },
        ],
        courseParticipants: [],
        selectedParticipants: [],
        trainingSessions: [],
        searchInput: '',
        trainingSessionFilter: '',
      }
    },
    computed: {
      today () {
        return this.$moment().format('YYYY-MM-DD HH:mm:ss')
      },
      filteredParticipants () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
  
        let primaryFilterParticipants = this.courseParticipants.filter(participant => {
          return ((participant.employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1
        })
        
        let filteredTrainingSessions = this.customFilterTrainingSession(primaryFilterParticipants)

        return filteredTrainingSessions
      },
      columnSize () {
        return this.questionSection ? '4' : '12'
      },
      dialogSize () {
        return this.questionSection ? 1500 : 500
      },
      dataTableHeight () {
        return this.questionSection ? 350 : 300
      },
    },
    watch: {
      dialog: function (val) {
        if (val) {
          this.loadQuestionTypes()
          this.$nextTick(() => {
            this.loadTrainingSessions()
            this.loadQuestions()
            this.loadCourseParticipants()
          })
        }
      },
      open: {
        immediate: true,
        handler (value) {
          if (value === true) {
            this.dialog = true
          }
        },
      },
    },

    methods: {
      loadTrainingSessions () {
        this.$api.trainingSessions().list(this.exam.course_id)
          .then(res => {
            this.trainingSessions = _.orderBy(res.data, ['name'], ['asc'])
          })
      },
      addParticipants () {
        this.addParticipant = true

        this.$api.examParticipants().create(this.exam.id, { employee_uids: this.prepareParticipantFormData() })
          .then(res => {
            this.$snack({ message: 'Exam Candidates Updated Successfully' })

            this.addParticipant = false
            this.selectedParticipants = []
            this.loadCourseParticipants()
          })
      },
      removeParticipants () {
        this.removeParticipant = true
        this.$api.examParticipants().bulkDelete(this.exam.id, { employee_uids: this.prepareParticipantFormData() })
          .then(res => {
            this.$snack({ message: 'Exam Candidates Deleted Successfully' })
            this.removeParticipant = false
            this.selectedParticipants = []
            this.loadCourseParticipants()
          })
      },
      loadCourseParticipants () {
        this.tableLoading = true
        this.$api.courseParticipants().list(this.exam.course_id)
          .then(response => {
            this.$api.examParticipants().list(this.exam.id)
              .then(res => {
                res.data.length > 0 ? this.questionSection = true : this.questionSection = false

                response.data.map(data => {
                  if (res.data.includes(data.employee_uid)) {
                    data.participated = true
                  } else {
                    data.participated = false
                  }
                })
                this.courseParticipants = _.orderBy(_.uniqBy(response.data, 'employee_uid'), ['participated', 'employee.person.name'], ['desc', 'asc'])
                this.tableLoading = false
              })
          })
      },
      loadQuestionTypes () {
        this.$api.questionTypes().list()
          .then(res => {
            this.questionTypes = _.orderBy(res.data, ['name'], ['asc'])
          })
      },
      loadQuestions () {
        this.loadQuestion = true

        this.$api.examQuestions().list(this.exam.id)
          .then(res => {
            if (res.data.length > 0) {
              let totalMarks = res.data.map(a => a.mark)
                .reduce((a, b) => {
                  return a + b
                })

              let totalMarksBeforeLastQuestion = 0
              res.data.map((data, index) => {
                if (index === res.data.length - 1) {
                  data.weightage = (100 - totalMarksBeforeLastQuestion).toFixed(2)
                } else {
                  data.weightage = ((data.mark / totalMarks) * 100).toFixed(2)
                  totalMarksBeforeLastQuestion = totalMarksBeforeLastQuestion + parseFloat(((data.mark / totalMarks) * 100).toFixed(2))
                }
              })

              this.questions = res.data
            } else {
              this.addQuestionSection = true
            }
            this.loadQuestion = false
          })
      },
      editQuestion (question) {
        this.localExamQuestion = { ...question }
        this.edit = true
      },
      update () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.$api.examQuestions().update(this.localExamQuestion.id, this.prepareUpdateFormData())
              .then(res => {
                if (res.status === 200) { 
                  this.loadQuestions()
                  this.$snack({ message: 'Question Updated Successfully' })
                  this.edit = false
                }
              })
          })
      },
      save (nextQuestion) {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
          
            this.$api.examQuestions().create(this.exam.id, this.prepareFormData())
              .then(res => {
                if (res.status === 201) {
                  this.$snack({ message: 'Question Added Successfully' })

                  if (nextQuestion) {
                    this.clearForm()
                  } else {
                    this.clearForm()
                    this.addQuestionSection = false
                  }
                  this.loadQuestions()
                }
              })
          })
      },
      addRow (edit) {
        if (edit) {
          this.localExamQuestion.answer_choices.push({ content: null })
        } else {
          this.answerChoices.push({ content: null })
        }
      },
      deleteRow (index, edit) {
        if (edit) {
          this.localExamQuestion.answer_choices.splice(index, 1)
        } else {
          this.answerChoices.splice(index, 1)
        }
      },
      clearAnswer (edit) {
        if (edit) {
          if (this.localExamQuestion.question_type_id === 1) {
            this.localExamQuestion.answer_choices = [{ content: null }, { content: null }]
          }
          if (this.localExamQuestion.question_type_id === 2) {
            this.localExamQuestion.answer_choices = [{ index: 0, content: null }]
          }
        } else {
          if (this.questionType === 1) {
            this.structureAnswer = null
          } 
          if (this.questionType === 2) {
            this.answerChoices = [{ content: null }, { content: null }]
          }
        }
      },
      prepareFormData () {
        this.answerChoices.map((answer, index) => {
          answer.index = index
        })

        return {
          'content': this.content,
          'question_type_id': this.questionType,
          'answer_index': this.questionType === 1 ? this.answerIndex : 0,
          'mark': this.mark,
          'choices': this.questionType === 1 ? this.answerChoices : [{ 'index': 0, 'content': this.structureAnswer }],
        }
      },
      prepareUpdateFormData () {
        this.localExamQuestion.answer_choices.map((answer, index) => {
          answer.index = index
        })
        return {
          'content': this.localExamQuestion.content,
          'question_type_id': this.localExamQuestion.question_type_id,
          'answer_index': this.localExamQuestion.question_type_id === 1 ? this.localExamQuestion.answer_index : 0,
          'mark': this.localExamQuestion.mark,
          'choices': this.localExamQuestion.answer_choices,
        }
      },
      clearQuestionForm () {
        this.answerIndex = null
        this.questionType = null
        this.content = null
        this.mark = null
        this.structureAnswer = null
        this.answerChoices = [{ content: null }, { content: null }]
        this.addQuestionSection = false
      },
      clearForm () {
        this.questionType = null
        this.content = null
        this.mark = null
        this.structureAnswer = null
        this.answerIndex = null
        this.answerChoices = [{ content: null }, { content: null }]

        this.$validator.reset()
      },
      closeDialog () {
        this.dialog = false
        this.addQuestionSection = false
        this.questionSection = false
        this.courseParticipants = []
        this.selectedParticipants = []
      },
      prepareParticipantFormData () {
        let selectedParticipantIds = []
        this.selectedParticipants.map(item => {
          selectedParticipantIds.push(item.employee_uid)
        })
        return selectedParticipantIds 
      },
      customFilterTrainingSession (employeesList) {
        if (this.trainingSessionFilter.length === 0) return employeesList
   
        return employeesList.filter(participant => {
          let trainingSessionId = (participant.training_session || {}).id

          return _.intersection([trainingSessionId], this.trainingSessionFilter).length > 0
        })
      },
    },
  }
</script>

<style scoped>
.dialog-size{
  min-height:600px;
  max-height:600px
}
.font-12{
  font-size:12px;
}
.drop-down>>>.v-input__control .v-input__slot .v-select__slot .v-select__selections {
  min-height:32px !important;
  max-height:32px !important;
  color:red !important;
}
 .radio-group-full-width >>>.v-input__control {
     width: 100%
 }
 .full-width{
   width: 100%;
 }
 .display-flex {
   display:flex
 }
 .correct-answer{
   background-color: #ebfaeb;
 }
 .border{
  border-left:1px solid #dcdedf;
  border-right:1px solid #dcdedf
 }
 .question-container{
  min-height:540px;
  max-height:540px;
  overflow-y: scroll
 }
 .question-container-scrollbar::-webkit-scrollbar {
  width: 3px !important; 
}
/* Track */
.question-container-scrollbar::-webkit-scrollbar-track {
  box-shadow: inset 0 0 2px #e6e6e6; 
  border-radius: 10px;
}
/* Handle */
.question-container-scrollbar::-webkit-scrollbar-thumb {
  background: grey; 
  border-radius: 10px;
  height: 2px;
}
/* Handle on hover */
.question-container-scrollbar::-webkit-scrollbar-thumb:hover {
  background:  #555; 
}
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
 .choices-scrollbar::-webkit-scrollbar {
  width: 3px !important; 
}
.choices-scrollbar::-webkit-scrollbar-track {
  box-shadow: inset 0 0 2px #e6e6e6; 
  border-radius: 10px;
}
.choices-scrollbar::-webkit-scrollbar-thumb {
  background: grey; 
  border-radius: 10px;
  height: 2px;
}
.choices-scrollbar::-webkit-scrollbar-thumb:hover {
  background:  #555; 
}
.choices-container{
  max-height:230px;
  overflow-y:scroll
}
.divider{
  border-right:1px solid #efefef;
  min-height:600px
}
.progress-circular-container{
  position: absolute;
  left: 65%;
  top:50%;
}
</style>
